import { CalendarDays, Heart, Home, TrendingUp } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { userRewardApi } from '../api/reward.api';
import type { AttendanceStatusResponse, PointBalanceResponse, LeaderboardRewardItem } from '../types/Reward.api.type';

interface TabItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  showRedDot?: boolean;
}

const TabBar = ({ variant = 'mobile' }: { variant?: 'mobile' | 'desktop' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const isDesktop = variant === 'desktop';

  /** 출석 미완료 여부 */
  const { data: attendanceStatus } = useQuery<AttendanceStatusResponse>({
    queryKey: ['attendanceStatus'],
    queryFn: () => userRewardApi.getAttendanceStatus().then((res) => res.data.results),
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 5,
  });

  /** 포인트 잔액 */
  const { data: pointBalance } = useQuery<PointBalanceResponse>({
    queryKey: ['pointBalance'],
    queryFn: () => userRewardApi.getPointBalance().then((res) => res.data.results),
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 5,
  });

  /** 리더보드 미수령 보상 */
  const { data: leaderboardRewards } = useQuery<LeaderboardRewardItem[]>({
    queryKey: ['leaderboardRewards'],
    queryFn: () => userRewardApi.getLeaderboardRewards().then((res) => res.data),
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 5,
  });

  /** RedDot 판별: 출석 미완료 OR 미수령 보상 OR balance > 0 */
  const hasAttendanceRedDot =
    isAuthenticated &&
    ((attendanceStatus && !attendanceStatus.attendedToday) ||
      (leaderboardRewards && leaderboardRewards.some((r) => !r.claimed)) ||
      (pointBalance && pointBalance.balance > 0));

  const tabs: TabItem[] = [
    {
      id: 'home',
      label: '홈',
      icon: Home,
      path: '/',
    },
    {
      id: 'popular',
      label: '인기설문',
      icon: TrendingUp,
      path: '/question/popular',
    },
    {
      id: 'attendance',
      label: '출석',
      icon: CalendarDays,
      path: '/user/dashboard?tab=attendance',
      showRedDot: !!hasAttendanceRedDot,
    },
    {
      id: 'myquestions',
      label: '내 설문',
      icon: Heart,
      path: '/question/myquestions',
    },
  ];

  const handleTabClick = (tab: TabItem) => {
    if (tab.id === 'attendance') {
      navigate('/user/dashboard', { state: { tab: 'attendance' } });
    } else {
      navigate(tab.path);
    }
  };
  const isActiveTab = (tab: TabItem) => {
    if (tab.path === '/') {
      return location.pathname === '/';
    }
    if (tab.id === 'attendance') {
      return location.pathname === '/user/dashboard' && location.state?.tab === 'attendance';
    }
    return location.pathname.startsWith(tab.path);
  };
  return (
    <div
      className={
        isDesktop ? 'fixed right-6 top-1/2 -translate-y-1/2 z-50' : 'fixed bottom-0 left-0 right-0 z-50 safe-bottom'
      }
    >
      <div className={isDesktop ? '' : 'px-4 pb-6'}>
        <div
          className={`bg-white/95 backdrop-blur-md border border-gray-100 rounded-2xl shadow-lg shadow-gray-200/50 ${
            isDesktop ? '' : 'mx-auto max-w-md'
          }`}
        >
          <div
            className={
              isDesktop ? 'flex flex-col items-stretch gap-1 px-2 py-3' : 'flex items-center justify-around px-2 py-3'
            }
          >
            {tabs.map((tab) => {
              const isActive = isActiveTab(tab);
              const IconComponent = tab.icon;

              return (
                <button
                  type="button"
                  key={tab.id}
                  onClick={() => handleTabClick(tab)}
                  className={`relative flex flex-col items-center justify-center min-w-0 ${isDesktop ? 'w-full' : 'flex-1'} px-3 py-2 rounded-xl transition-all duration-300 ${isActive ? 'bg-blue-50 text-blue-600 transform scale-105' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
                >
                  <div
                    className={`relative transition-all duration-300 ${isActive ? 'transform -translate-y-0.5' : ''}`}
                  >
                    <IconComponent
                      className={`w-5 h-5 mb-1 transition-all duration-300 ${isActive ? 'text-blue-600' : 'text-gray-500'}`}
                    />
                    {/* RedDot */}
                    {tab.showRedDot && <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />}
                  </div>
                  <span
                    className={`text-xs font-medium transition-all duration-300 ${isActive ? 'text-blue-600' : 'text-gray-500'}`}
                  >
                    {tab.label}
                  </span>
                  {isActive && (
                    <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-blue-600 rounded-full animate-scale-in" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TabBar;
